"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useI18n } from "@/i18n/I18nProvider";
import { META_EVENT, purchaseEventId, type ConsentChoice, type FunnelEvent, type SiteTrackingPublic, type TrackingIds } from "@/lib/tracking/shared";
import { captureUtm, ensureFbq, ensureGtag, firstTimeInBrowser, readConsent, updateGtagConsent, writeConsent } from "@/lib/tracking/browser";

/**
 * Google Analytics 4 and the Meta Pixel on public pages: the landing page, booking sites and their payment pages.
 * Villa OS's own IDs load everywhere public; a booking site adds its owner's IDs (useSiteTracking). The staff app and the
 * guest portal never load anything.
 *
 * Consent: GA runs in Consent Mode (cookieless pings until the guest accepts); the Meta Pixel only loads after they accept.
 */

export interface FunnelItem {
  item_id: string;
  item_name: string;
  price?: number;
  quantity?: number;
}

export interface FunnelData {
  /** Major units (12.34, not 1234); see fromMinorUnits. */
  value?: number;
  currency?: string;
  items?: FunnelItem[];
  search_term?: string;
  /** generate_lead: what kind of lead (whatsapp, activity_booking). */
  lead_type?: string;
}

export interface PurchaseData {
  reference: string;
  value: number;
  currency: string;
  item: FunnelItem;
  /** GA4 properties that get this purchase from the server; skipped here so GA counts it once. */
  serverGa4Ids: string[];
}

interface TrackingApi {
  track: (event: Exclude<FunnelEvent, "purchase">, data?: FunnelData) => void;
  trackPurchase: (data: PurchaseData) => void;
  setSite: (site: SiteTrackingPublic | null | undefined) => void;
  /** Shows the consent banner again, so a guest can change their mind. */
  openConsentSettings: () => void;
}

const noop: TrackingApi = { track: () => {}, trackPurchase: () => {}, setSite: () => {}, openConsentSettings: () => {} };
const TrackingContext = createContext<TrackingApi>(noop);

/** Paths of the main app domain that are public pages. On a booking site's own domain every page is public. */
function isPublicPath(pathname: string, isSiteHost: boolean): boolean {
  if (pathname.startsWith("/guest") || pathname.startsWith("/q/")) return false;
  if (isSiteHost) return true;
  return pathname === "/landing" || pathname.startsWith("/site/") || pathname.startsWith("/pay/booking/");
}

export function TrackingProvider({ platform, isSiteHost, children }: { platform: TrackingIds; isSiteHost: boolean; children: React.ReactNode }) {
  const pathname = usePathname() || "/";
  const active = isPublicPath(pathname, isSiteHost);
  const [consent, setConsent] = useState<ConsentChoice | null | undefined>(undefined); // undefined: not read yet
  const [site, setSiteState] = useState<SiteTrackingPublic | null>(null);

  // Imperative state: what has been configured on this page so far.
  const consentRef = useRef<ConsentChoice | null>(null);
  const consentRead = useRef(false);
  const siteRef = useRef<SiteTrackingPublic | null>(null);
  const gaConfigured = useRef(new Set<string>());
  const pixelsInitialized = useRef(new Set<string>());
  const lastPath = useRef<string | null>(null);

  const gaIds = useCallback(() => [platform.ga4_id, siteRef.current?.ga4_id].filter((x): x is string => Boolean(x)), [platform.ga4_id]);
  const pixelIds = useCallback(
    () => [platform.meta_pixel_id, siteRef.current?.meta_pixel_id].filter((x): x is string => Boolean(x)),
    [platform.meta_pixel_id]
  );

  /** Loads and configures whatever is known now; safe to call repeatedly. Villa OS's IDs always come first. */
  const sync = useCallback(() => {
    if (!active) return;
    // A page's own effects can run before this provider's, so the cookie is read by whichever comes first.
    if (!consentRead.current) {
      consentRef.current = readConsent();
      consentRead.current = true;
    }
    const granted = consentRef.current === "granted";
    const ga = gaIds();
    if (ga.length) {
      const gtag = ensureGtag(ga[0], granted);
      for (const id of ga) {
        if (gaConfigured.current.has(id)) continue;
        gaConfigured.current.add(id);
        gtag("config", id); // sends this property's page_view
      }
    }
    const pixels = pixelIds();
    if (granted && pixels.length) {
      const fbq = ensureFbq();
      for (const id of pixels) {
        if (pixelsInitialized.current.has(id)) continue;
        pixelsInitialized.current.add(id);
        fbq("init", id);
        fbq("trackSingle", id, "PageView");
      }
    }
  }, [active, gaIds, pixelIds]);

  useEffect(() => {
    if (!active) return;
    sync();
    setConsent(consentRef.current);
    captureUtm();
  }, [active, sync]);

  // Client-side navigation between public pages: a page view for everything already configured.
  useEffect(() => {
    if (!active) return;
    if (lastPath.current !== null && lastPath.current !== pathname) {
      window.gtag?.("event", "page_view", { page_location: window.location.href, page_title: document.title });
      if (pixelsInitialized.current.size) window.fbq?.("track", "PageView");
    }
    lastPath.current = pathname;
  }, [active, pathname]);

  const choose = useCallback(
    (choice: ConsentChoice) => {
      writeConsent(choice);
      consentRef.current = choice;
      consentRead.current = true;
      setConsent(choice);
      updateGtagConsent(choice === "granted");
      window.fbq?.("consent", choice === "granted" ? "grant" : "revoke");
      sync();
    },
    [sync]
  );

  const api = useMemo<TrackingApi>(() => {
    const setSite: TrackingApi["setSite"] = (next) => {
      const value = next && (next.ga4_id || next.meta_pixel_id) ? next : null;
      siteRef.current = value;
      setSiteState(value);
      sync();
    };
    const track: TrackingApi["track"] = (event, data = {}) => {
      if (!active) return;
      sync();
      const params: Record<string, unknown> = {};
      if (data.value !== undefined) params.value = data.value;
      if (data.currency) params.currency = data.currency;
      if (data.items) params.items = data.items.map((i) => ({ quantity: 1, ...i }));
      if (data.search_term) params.search_term = data.search_term;
      if (data.lead_type) params.lead_type = data.lead_type;
      if (gaIds().length) window.gtag?.("event", event, params);
      if (pixelsInitialized.current.size) {
        const custom: Record<string, unknown> = {};
        if (data.value !== undefined) custom.value = data.value;
        if (data.currency) custom.currency = data.currency;
        if (data.items?.length) {
          custom.content_ids = data.items.map((i) => i.item_id);
          custom.content_name = data.items[0].item_name;
          custom.content_type = "product";
          custom.contents = data.items.map((i) => ({ id: i.item_id, quantity: i.quantity ?? 1 }));
        }
        if (data.search_term) custom.search_string = data.search_term;
        if (data.lead_type) custom.content_category = data.lead_type;
        window.fbq?.("track", META_EVENT[event], custom);
      }
    };
    const trackPurchase: TrackingApi["trackPurchase"] = (p) => {
      if (!active || !firstTimeInBrowser(`vos_purchase_${p.reference}`)) return;
      sync();
      const item = { quantity: 1, price: p.value, ...p.item };
      const browserGa = gaIds().filter((id) => !p.serverGa4Ids.includes(id));
      if (browserGa.length) {
        window.gtag?.("event", "purchase", { send_to: browserGa, transaction_id: p.reference, value: p.value, currency: p.currency, items: [item] });
      }
      if (pixelsInitialized.current.size) {
        window.fbq?.(
          "track",
          "Purchase",
          { value: p.value, currency: p.currency, order_id: p.reference, content_ids: [item.item_id], content_name: item.item_name, content_type: "product", num_items: 1 },
          { eventID: purchaseEventId(p.reference) }
        );
      }
    };
    const openConsentSettings = () => setConsent(null);
    return { setSite, track, trackPurchase, openConsentSettings };
  }, [active, sync, gaIds]);

  const hasAnyId = Boolean(platform.ga4_id || platform.meta_pixel_id || site);
  return (
    <TrackingContext.Provider value={api}>
      {children}
      {active && hasAnyId && consent === null && <ConsentBanner onChoose={choose} />}
    </TrackingContext.Provider>
  );
}

export function useTracking(): TrackingApi {
  return useContext(TrackingContext);
}

/** A public page that belongs to one booking site: adds its owner's GA4 / Meta Pixel for as long as the page is shown. */
export function useSiteTracking(site: SiteTrackingPublic | null | undefined): void {
  const { setSite } = useTracking();
  const ga = site?.ga4_id ?? null;
  const pixel = site?.meta_pixel_id ?? null;
  useEffect(() => {
    if (ga || pixel) setSite({ ga4_id: ga, meta_pixel_id: pixel });
  }, [ga, pixel, setSite]);
}

function ConsentBanner({ onChoose }: { onChoose: (choice: ConsentChoice) => void }) {
  const { t } = useI18n();
  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t("publicsite.consent.label")}
      style={{
        position: "fixed",
        left: 16,
        bottom: 16,
        zIndex: 2147483000,
        maxWidth: 380,
        width: "calc(100% - 32px)",
        boxSizing: "border-box",
        padding: "14px 16px",
        borderRadius: 12,
        background: "#ffffff",
        color: "#1f2937",
        border: "1px solid #e5e7eb",
        boxShadow: "0 10px 30px rgba(15, 23, 42, 0.18)",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      <p style={{ margin: "0 0 12px" }}>{t("publicsite.consent.text")}</p>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => onChoose("denied")}
          style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #d1d5db", background: "#ffffff", color: "#1f2937", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          {t("publicsite.consent.decline")}
        </button>
        <button
          type="button"
          onClick={() => onChoose("granted")}
          style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid #111827", background: "#111827", color: "#ffffff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}
        >
          {t("publicsite.consent.accept")}
        </button>
      </div>
    </div>
  );
}
